import React, { createContext, useContext, useState, useEffect } from "react";
import { auth } from "./firebase";
import { getUserDetails, getClubs } from "./api";

export const AuthContext = createContext(null);

export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [roles, setRoles] = useState([]);
    const [clubName, setClubName] = useState(null);
    const [theme, setTheme] = useState({ bgColor: "#ffffff", fgColor: "#000000" });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
            setUser(firebaseUser);
            if (firebaseUser) {
                try {
                    // Fetch user details and clubs in parallel for faster loading
                    const [userDetails, clubsData] = await Promise.all([
                        getUserDetails(),
                        getClubs()
                    ]);

                    const userRoles = userDetails.roles
                        ? userDetails.roles
                        : (userDetails.role ? [userDetails.role] : []);
                    setRoles(userRoles.length > 0 ? userRoles : ["default"]);
                    setClubName(userDetails.club || "RugbyCore");

                    if (userDetails.club) {
                        const matchingClub = clubsData.find((c) => c.name === userDetails.club);
                        if (matchingClub) {
                            setTheme({
                                bgColor: matchingClub.bgColor || "#ffffff",
                                fgColor: matchingClub.fgColor || "#000000",
                            });
                        }
                    }
                } catch (error) {
                    console.error("Error fetching user details:", error);
                    setRoles(["default"]);
                    setClubName("RugbyCore");
                }
            } else {
                setRoles([]);
                setClubName(null);
                setTheme({ bgColor: "#ffffff", fgColor: "#000000" });
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    return (
        <AuthContext.Provider value={{ user, roles, clubName, theme, loading }}>
            {children}
        </AuthContext.Provider>
    );
};
